import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { auth } from '../../../firebase';
import { ShieldCheck, Lock } from 'lucide-react';
import './UserID.css';
import { API_BASE } from "../../../config/api";

const UserID = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFlipped, setIsFlipped] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        await fetchUserData();
      } else {
        setUser(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // ✅ FIXED: Now uses JWT token authentication
  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get token from localStorage
      const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      // ✅ Use current-user endpoint with JWT token
      const response = await axios.get(`${API_BASE}/api/identity/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        console.log("✅ User data loaded:", response.data.user);
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      console.error("❌ Failed to fetch user data:", error);
      setError(error.response?.data?.message || error.message || 'Failed to load user data');
      
      // If auth error, redirect to login
      if (error.response?.status === 401) {
        localStorage.clear();
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="erax-id-wrapper">
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
          <p>Loading ID Card...</p>
        </div>
      </div>
    );
  }

  if (!user || error) {
    return (
      <div className="erax-id-wrapper">
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
          <p>{error || 'Unable to load ID Card'}</p>
          <button 
            onClick={fetchUserData}
            style={{ 
              marginTop: '10px', 
              padding: '8px 16px', 
              background: '#1e293b', 
              border: 'none', 
              borderRadius: '6px', 
              color: '#fff', 
              cursor: 'pointer' 
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // 1. Safely extract ALL data from the backend user object
  console.log("📋 User data for ID card:", user);
  
  const safeUser = user || {};
  const name = safeUser.fullName || safeUser.firstName || safeUser.lastName 
    ? `${safeUser.firstName || ''} ${safeUser.lastName || ''}`.trim() 
    : 'EraX Member';
  const email = safeUser.email || 'N/A';
  const role = safeUser.isAdmin ? 'System Admin' : 'Verified Member';
  const location = safeUser.location || 'Global Network';
  const empId = safeUser.referralCode || (safeUser.id ? safeUser.id.slice(-8).toUpperCase() : 'N/A');
  const photo = safeUser.photoURL 
    ? (safeUser.photoURL.startsWith('http') ? safeUser.photoURL : `${API_BASE}${safeUser.photoURL}`)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=f59e0b&color=0b1120&size=150&bold=true`;
  
  // Extract security & auth fields
  const isVerified = safeUser.isVerified ?? true;
  const twoStep = safeUser.twoStep ?? false;
  const authProvider = safeUser.authProvider || 'email';

  // Extract Balances & Stats with safe defaults
  const balances = safeUser.balances || {};
  const availableLiquidity = parseFloat(balances.availableLiquidity || 0);
  const totalDeposited = parseFloat(balances.totalDeposited || 0);
  const netProfitLoss = parseFloat(balances.netProfitLoss || 0);
  const referralCount = parseInt(balances.referralCount || 0);
  const referralEarnings = parseFloat(balances.referralEarnings || 0);
  const totalInvested = parseFloat(balances.totalInvested || 0);
  const totalWithdrawn = parseFloat(balances.totalWithdrawn || 0);
  
  // Extract Referred By
  const referredBy = safeUser.referredBy;
  const referrerName = referredBy?.name || 'Direct Sign-up';

  // 🎨 DYNAMIC THEME: Red for Admins, Orange for standard users
  const accentColor = safeUser.isAdmin ? "#ef4444" : "#f59e0b";
  
  // 📱 QR Code
  const qrData = `https://erax.company/verify/${safeUser.id || 'demo'}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}&bgcolor=ffffff&color=0b1120`;

  // Dates
  const joinDate = safeUser.createdAt 
    ? new Date(safeUser.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'N/A';
  const lastLogin = safeUser.lastLoginAt 
    ? new Date(safeUser.lastLoginAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit' })
    : 'Never';

  return (
    <div 
      className={`erax-id-wrapper ${isFlipped ? 'is-flipped' : ''}`} 
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <div className="erax-id-card">
        
        {/* ================= FRONT FACE (Identity & Security) ================= */}
        <div 
          className="erax-id-face erax-id-front" 
          style={{ '--accent': accentColor, '--accent-glow': `${accentColor}26` }}
        >
          <div className="erax-id-front__accent-line" />
          
          <div className="erax-id-front__header">
            <div className="erax-id-front__brand">
              <h2 className="erax-id-front__company">Era <span style={{color: accentColor}}>X</span></h2>
            </div>
            <div className="erax-id-front__badge">member ID</div>
          </div>

          <div className="erax-id-front__body">
            <div className="erax-id-front__photo-wrapper">
              <img src={photo} alt={name} className="erax-id-front__photo" />
              {isVerified && <div className="erax-id-front__status-dot" />}
            </div>

            <div className="erax-id-front__info">
              <h3 className="erax-id-front__name">{name}</h3>
              <p className="erax-id-front__role">{role}</p>
              
              {/* Security & Auth Badges */}
              <div className="erax-id-front__badges">
                {isVerified && (
                  <span className="erax-id-front__mini-badge green">
                    <ShieldCheck size={10} /> Verified
                  </span>
                )}
                {twoStep && (
                  <span className="erax-id-front__mini-badge blue">
                    <Lock size={10} /> 2FA
                  </span>
                )}
                <span className="erax-id-front__mini-badge purple">
                  {authProvider === 'google' ? 'Google' : 'Email'}
                </span>
              </div>

              <p className="erax-id-front__details" style={{marginTop: '8px'}}>
                <span>Email:</span> {email}<br />
                <span>Referral:</span> {empId}
              </p>
            </div>
          </div>

          <div className="erax-id-front__footer">
            <div className="erax-id-front__validity">
              Member Since
              <span className="erax-id-front__validity-date">{joinDate}</span>
            </div>
            <div className="erax-id-front__barcode" aria-hidden="true" />
          </div>
        </div>

        {/* ================= BACK FACE (Financials & Network) ================= */}
        <div 
          className="erax-id-face erax-id-back"
          style={{ '--accent': accentColor }}
        >
          <div className="erax-id-back__stripe" />
          
          <div className="erax-id-back__content" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: accentColor, fontSize: '14px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Digital Wallet & Network
              </h3>
              <div className="erax-id-back__qr">
                <img src={qrUrl} alt="QR Code" />
              </div>
            </div>

            {/* Grid for Balances and Stats */}
            <div className="erax-id-back__grid">
              <div className="erax-id-back__stat-box">
                <div className="erax-id-back__stat-label">Available Balance</div>
                <div className="erax-id-back__stat-value green">${availableLiquidity.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
              
              <div className="erax-id-back__stat-box">
                <div className="erax-id-back__stat-label">Net Profit / Loss</div>
                <div className={`erax-id-back__stat-value ${netProfitLoss >= 0 ? 'profit' : 'loss'}`}>
                  {netProfitLoss >= 0 ? '+' : ''}${netProfitLoss.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>

              <div className="erax-id-back__stat-box">
                <div className="erax-id-back__stat-label">Total Deposited</div>
                <div className="erax-id-back__stat-value">${totalDeposited.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>

              <div className="erax-id-back__stat-box">
                <div className="erax-id-back__stat-label">Referral Earnings</div>
                <div className="erax-id-back__stat-value green">${referralEarnings.toFixed(2)}</div>
              </div>

              {/* ✅ NEW: Additional Stats */}
              <div className="erax-id-back__stat-box">
                <div className="erax-id-back__stat-label">Total Invested</div>
                <div className="erax-id-back__stat-value">${totalInvested.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>

              <div className="erax-id-back__stat-box">
                <div className="erax-id-back__stat-label">Total Withdrawn</div>
                <div className="erax-id-back__stat-value">${totalWithdrawn.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
              </div>
            </div>

            {/* Bottom Info Row */}
            <div className="erax-id-back__info-row" style={{ width: '100%' }}>
              <div>Referrals: <span>{referralCount} Users</span></div>
              <div>Referred By: <span>{referrerName}</span></div>
            </div>
            <div className="erax-id-back__info-row" style={{ width: '100%', border: 'none', marginTop: '4px', paddingTop: '0' }}>
              <div>Last Login: <span>{lastLogin}</span></div>
              <div>Location: <span>{location}</span></div>
            </div>
          </div>

          <div className="erax-id-back__footer">
            EraX Corp • Secure Encrypted Connection • {new Date().getFullYear()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default UserID;